import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';

interface ProgressStep {
  id: number;
  label: string;
  completed: boolean;
  current: boolean;
}

interface ProgressIndicatorProps {
  currentStep: number;
  steps: string[];
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ currentStep, steps }) => {
  const progressSteps: ProgressStep[] = steps.map((step, index) => ({
    id: index + 1,
    label: step,
    completed: index < currentStep - 1,
    current: index === currentStep - 1,
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        mb: 4,
        px: 2,
        py: 2,
      }}
    >
      {progressSteps.map((step, index) => (
        <React.Fragment key={step.id}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: step.completed 
                  ? '#2196f3' 
                  : step.current 
                    ? '#2196f3' 
                    : '#e0e0e0',
                color: 'white',
                border: step.current ? '3px solid rgba(33, 150, 243, 0.3)' : 'none',
                boxShadow: step.current 
                  ? '0 0 0 4px rgba(33, 150, 243, 0.2)' 
                  : 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.1rem',
                }}
              >
                {step.id}
              </Typography>
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                color: step.completed || step.current ? '#2196f3' : '#757575',
                fontWeight: step.current ? 700 : 500,
                textAlign: 'center',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {step.label}
            </Typography>
          </Box>
          {index < progressSteps.length - 1 && (
            <Box
              sx={{
                width: 80,
                height: 3,
                backgroundColor: step.completed 
                  ? '#2196f3' 
                  : '#e0e0e0',
                borderRadius: 2,
                transition: 'all 0.3s ease',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default ProgressIndicator; 
